import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Briefcase, Users, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AdminLayout: React.FC = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/jobs', label: 'Job Postings', icon: Briefcase },
    { path: '/admin/applicants', label: 'Applicants', icon: Users },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        background: 'var(--pw-gradient-hero)', 
        color: 'var(--pw-text-main)',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      }}
    >
      {/* Sidebar */}
      <div 
        className="fixed inset-y-0 left-0 w-64 shadow-2xl backdrop-blur-sm" 
        style={{ 
          backgroundColor: 'rgba(17, 24, 39, 0.95)', 
          borderRight: '1px solid var(--pw-surface-secondary)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg" 
              style={{ background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-light) 100%)' }}
            >
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ color: 'var(--pw-text-main)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>PattrnWorks</h1>
              <p className="text-xs" style={{ color: 'var(--pw-muted-light)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>Admin Panel</p>
            </div>
          </div>

          <nav className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? 'shadow-lg'
                      : ''
                  }`}
                  style={isActive(item.path) 
                    ? { backgroundColor: 'var(--pw-primary)', color: 'var(--pw-text-main)' }
                    : { color: 'var(--pw-muted-light)', backgroundColor: 'transparent' }
                  }
                  onMouseEnter={(e) => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6" style={{ borderTop: '1px solid var(--pw-surface-secondary)' }}>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg transition-all duration-200"
            style={{ color: 'var(--pw-muted-light)', backgroundColor: 'transparent' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64">
        {/* Header */}
        <header 
          className="backdrop-blur-sm sticky top-0 z-10"
          style={{ 
            backgroundColor: 'rgba(31, 41, 55, 0.8)', 
            borderBottom: '1px solid var(--pw-surface-tertiary)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div className="px-8 py-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 
                  className="text-3xl font-bold" 
                  style={{ 
                    color: 'var(--pw-text-main)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    letterSpacing: '-0.02em'
                  }}
                >
                  {navItems.find((item) => isActive(item.path))?.label || 'Admin'}
                </h2>
                <p 
                  className="text-sm mt-1" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Manage your platform efficiently
                </p>
              </div>
              <Link
                to="/careers"
                className="px-5 py-2.5 rounded-lg transition-all duration-200 font-medium"
                style={{ 
                  backgroundColor: 'var(--pw-surface-tertiary)', 
                  color: 'var(--pw-text-main)',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--pw-primary)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                View Site
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;

